import { forwardRef, useImperativeHandle, useRef } from "react"
import { FileText, Trash2 } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"

// Standardized file picker used across the app.
//
// Three visual states are layered automatically based on props:
//   1. Blue chip — user picked a new File but hasn't saved yet (`value` set)
//   2. Amber chip — user clicked Remove on an existing attachment (`pendingRemoval`)
//   3. Zinc chip — an attachment is already saved (`existingFileName`/`existingFileUrl`)
// When none of those apply, the bare file input shows.
//
// Pure-create flows (no existing file, no "remove" affordance) only use
// state #1 — so callers can omit the existing-file/pending-removal props
// and still get the consistent "selected file" chip after the user picks.
export const FileUploadField = forwardRef(function FileUploadField(
  {
    id,
    accept,
    value,
    onChange,
    existingFileName,
    existingFileUrl,
    pendingRemoval = false,
    onRequestRemoveExisting,
    onUndoRemoveExisting,
    helpText,
    previewSrc,
    inputClassName = "bg-white border-zinc-300 text-zinc-900",
    disabled = false,
  },
  ref
) {
  const inputRef = useRef(null)

  // Expose the input ref so callers can clear it imperatively if needed.
  useImperativeHandle(ref, () => ({
    clear() {
      onChange?.(null)
      if (inputRef.current) inputRef.current.value = ""
    },
    focus() {
      inputRef.current?.focus()
    },
  }))

  const hasNewFile = !!value
  const hasExisting = !!existingFileName || !!existingFileUrl || !!previewSrc
  const showInput = !hasNewFile

  return (
    <div className="space-y-2">
      {hasNewFile ? (
        <div className="flex items-center gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
          <FileText className="h-4 w-4 text-blue-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-900 truncate">{value.name}</p>
            <p className="text-xs text-blue-700">
              New file selected — save the form to upload.
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-zinc-600 hover:text-zinc-900"
            onClick={() => {
              onChange?.(null)
              if (inputRef.current) inputRef.current.value = ""
            }}
            disabled={disabled}
          >
            Clear
          </Button>
        </div>
      ) : pendingRemoval ? (
        <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <Trash2 className="h-4 w-4 text-amber-600 shrink-0" />
          <p className="flex-1 text-sm text-amber-800">
            File will be removed when you save.
          </p>
          {onUndoRemoveExisting && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-zinc-600 hover:text-zinc-900"
              onClick={onUndoRemoveExisting}
              disabled={disabled}
            >
              Undo
            </Button>
          )}
        </div>
      ) : hasExisting ? (
        <div className="flex items-center gap-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
          {previewSrc ? (
            <img
              src={previewSrc}
              alt=""
              className="h-10 w-10 rounded object-cover border border-zinc-200 shrink-0"
            />
          ) : (
            <FileText className="h-4 w-4 text-zinc-500 shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-zinc-900 truncate">
              {existingFileName || (previewSrc ? "Current image" : "Attached file")}
            </p>
            {existingFileUrl && (
              <a
                href={existingFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline"
              >
                {previewSrc ? "View image" : "View file"}
              </a>
            )}
          </div>
          {onRequestRemoveExisting && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={onRequestRemoveExisting}
              disabled={disabled}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Remove
            </Button>
          )}
        </div>
      ) : null}

      {showInput && (
        <Input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          className={inputClassName}
          disabled={disabled}
          onChange={(e) => {
            const f = e.target.files?.[0] || null
            onChange?.(f)
            // Picking a file overrides any pending removal of the existing file.
            if (f && onUndoRemoveExisting && pendingRemoval) onUndoRemoveExisting()
          }}
        />
      )}

      {helpText && (
        <p className="text-xs text-zinc-400">
          {hasExisting && !hasNewFile && !pendingRemoval
            ? `Upload a new file to replace the current one. ${helpText}`
            : helpText}
        </p>
      )}
    </div>
  )
})
